import type { AdaptationSuggestion } from '../dto/AdaptationSuggestion';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { AppPreferencesRepository } from '../ports/AppPreferencesRepository';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { IdGenerator } from '../ports/IdGenerator';
import { runAdaptationCycle } from '../../domain/adaptive-guidance/AdaptationCycle';
import { UserChoice } from '../../domain/adaptive-guidance/enums';
import {
  DEFAULT_ADAPTIVE_GUIDANCE_PREFERENCES,
  fromAdaptiveGuidancePreferencesData,
  toAdaptiveGuidancePreferencesData,
} from '../../domain/preferences/AdaptiveGuidancePreferences';
import type { AppPreferencesId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';
import type { ReorderDailySteps } from './ReorderDailySteps';

/**
 * Records the user's explicit choice (Decision preseance #3: the current explicit choice
 * outranks the proposal) and, only on acceptance, applies the already-computed proposed order
 * through the existing reorder mechanism — the "moteur d'adaptation externe" required by
 * adaptive-guidance/COGNITIVE_MODEL.md. A rejection never touches routine data; nothing is ever
 * applied without this explicit call.
 */
export class RespondToAdaptationSuggestion {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly appPreferences: AppPreferencesRepository,
    private readonly reorder: ReorderDailySteps,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
    choice: UserChoice,
  ): Promise<Result<AdaptationSuggestion, RoutineError>> {
    try {
      const routine = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (!routine) return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      const steps = await this.dailySteps.listByRoutine(routine.id);
      const preferencesEntity = await this.appPreferences.findByProfileId(profileId);
      const preferences =
        (preferencesEntity && fromAdaptiveGuidancePreferencesData(preferencesEntity.data)) ??
        DEFAULT_ADAPTIVE_GUIDANCE_PREFERENCES;

      const cycle = runAdaptationCycle({
        dayMode: routine.dayMode,
        steps,
        timestamp: this.clock.now().toISOString(),
        agsEnabled: preferences.agsEnabled,
      });

      const timestamp = this.clock.now().toISOString();
      const updatedPreferences = {
        ...preferences,
        decisions: { ...preferences.decisions, [logicalDate]: choice },
      };
      const preferencesRecord = preferencesEntity
        ? { ...preferencesEntity, data: toAdaptiveGuidancePreferencesData(updatedPreferences), updatedAt: timestamp }
        : {
            id: this.ids.generate<AppPreferencesId>(),
            profileId,
            locale: 'fr-FR',
            data: toAdaptiveGuidancePreferencesData(updatedPreferences),
            createdAt: timestamp,
            updatedAt: timestamp,
          };
      await this.appPreferences.save(preferencesRecord);

      if (choice === UserChoice.accepted && cycle.decision.option) {
        const applied = await this.reorder.execute(
          profileId,
          logicalDate,
          cycle.decision.option.proposedOrder,
          'today',
        );
        if (!applied.ok) return failure(applied.error);
      }

      const respected = runAdaptationCycle({
        dayMode: routine.dayMode,
        steps,
        timestamp,
        agsEnabled: preferences.agsEnabled,
        priorChoice: choice,
      });
      return success({
        decisionType: respected.decision.type,
        guidanceCode: respected.explanation.code,
        modeKey: respected.explanation.modeKey,
        affectedCount: respected.explanation.affectedCount,
        ...(respected.decision.option
          ? {
              ruleId: respected.decision.option.ruleId,
              proposedOrder: respected.decision.option.proposedOrder,
              affectedStepIds: respected.decision.option.affectedStepIds,
            }
          : {}),
      });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
